/* global route */

import debounce from 'lodash.debounce';
import mapValues from 'lodash.mapvalues';
import md5 from 'md5';
import axios from 'axios';
import qs from 'qs';
import { isResearch } from '@/utils/routing/routing';

function generateIndexKey(uids, options, series_restriction) {
  return md5(uids.join(',') + JSON.stringify(options ?? {}) + (series_restriction ?? ''));
}

export async function generateViewerUrl({
  studies,
  caseId,
  readOnly = 0,
  segmentation = 0,
  autoloadSegmentations,
  segmentationUids,
  segmentationOptions,
  skipRestrictions,
  seriesRestriction,
}) {
  const params = {
    studies,
    caseId,
    read_only: readOnly ? 1 : 0,
    segmentation: segmentation ? 1 : 0,
    autoload_segmentation: autoloadSegmentations ? autoloadSegmentations : undefined,
    segmentationUids,
    segmentationOptions,
    skip_restrictions: skipRestrictions ? 'true' : 'false',
    series_restriction: seriesRestriction,
  };

  if (isResearch()) {
    params.skip_restrictions = Boolean(skipRestrictions);
    return axios.post(route('post-viewer-url'), params);
  } else {
    return axios.get(route('get-viewer-url', params));
  }
}

function generateUrlFunction(
  uids,
  readOnly = false,
  segmentation = false,
  segmentationUids = [],
  segmentationOptions = undefined,
  autoloadSegmentations = false,
  skipRestrictions = false,
  series_restriction = []
) {
  return async function () {
    if (!uids || !uids.length) {
      return null;
    }

    try {
      const response = await generateViewerUrl({
        studies: uids,
        readOnly: readOnly ? 1 : 0,
        segmentation: segmentation ? 1 : 0,
        autoloadSegmentations: autoloadSegmentations ? autoloadSegmentations : undefined,
        segmentationUids,
        segmentationOptions,
        skipRestrictions: skipRestrictions ? 'true' : 'false',
        seriesRestriction: series_restriction,
      });

      const { url } = await response.data.data;
      return url;
    } catch (error) {
      console.error(error);
      return null;
    }
  };
}

function generateViewerEventUrl(uids, caseId = null) {
  return async function () {
    return route('put-viewer-opened', { studies: uids, caseId });
  };
}

const TIMEOUT = 120000;

class DicomViewerRepository {
  #index = {};
  #intervals = {};
  #urls = {};
  #eventUrls = {};

  #store = null;
  #registeredKeys = [];

  constructor() {
    this.updateStore = debounce(() => {
      if (this.#store) {
        const tokens = mapValues(this.#urls, (url) => {
          let queryString = {};

          if (url) {
            queryString = qs.parse(url.split('?')[1] || '');
          }

          return queryString.token || null;
        });

        this.#store.commit('updateTokens', tokens);
        this.#store.commit('updateUrls', this.#urls);
        this.#store.commit('updateViewerEventUrl', this.#eventUrls);
      }
    }, 300);
  }

  async registerStudies(
    studies,
    readOnly = false,
    caseId = null,
    segmentation = false,
    segmentationUids = [],
    options = {}
  ) {
    if (studies && studies.length) {
      const uids = studies.map(({ studyInstanceUid }) => studyInstanceUid);
      let series_restriction = [];
      if (isResearch()) {
        series_restriction = studies.map(({ studyInstanceUid, seriesUids }) => ({
          study: studyInstanceUid,
          series: seriesUids?.filter((item) => item),
        }));
      }
      const indexKey = this.getIndexKey(studies, readOnly, options, series_restriction);
      if (!this.#registeredKeys.includes(indexKey)) {
        this.#registeredKeys = [...this.#registeredKeys, indexKey];
        this.#index[indexKey] = generateUrlFunction(
          uids,
          readOnly,
          segmentation,
          segmentationUids,
          options?.segmentationOptions ?? {},
          options?.autoloadSegmentations ?? false,
          options?.skipRestrictions ?? false,
          series_restriction
        );
        this.#urls[indexKey] = await this.#index[indexKey]();
        this.#eventUrls[indexKey] = await generateViewerEventUrl(uids, caseId)();

        this.#intervals[indexKey] = setInterval(async () => {
          this.#urls[indexKey] = await this.#index[indexKey]();
          this.updateStore();
        }, TIMEOUT);

        this.updateStore();
      }
    }
  }

  unregisterStudies(studies, readOnly = false, options = {}) {
    if (studies && studies.length) {
      const indexKey = this.getIndexKey(studies, readOnly, options);

      if (this.#intervals[indexKey]) {
        clearInterval(this.#intervals[indexKey]);
        delete this.#intervals[indexKey];
      }

      delete this.#urls[indexKey];
      delete this.#eventUrls[indexKey];

      this.#registeredKeys = this.#registeredKeys.filter((k) => k !== indexKey);

      this.updateStore();
    }
  }

  getIndexKey(studies, readOnly = false, options = {}, series_restriction = []) {
    if (!series_restriction.length && isResearch()) {
      series_restriction = studies.map(({ studyInstanceUid, seriesUids }) => ({
        study: studyInstanceUid,
        series: seriesUids?.filter((item) => item),
      }));
    }
    let key = generateIndexKey(
      studies.filter((item) => !!item).map(({ studyInstanceUid }) => studyInstanceUid),
      options,
      series_restriction?.map(({ series }) => series?.join(','))?.join(',')
    );

    if (readOnly) {
      key = `ro-${key}`;
    }

    return key;
  }

  registerStore(store) {
    this.#store = store;
  }
}

export default new DicomViewerRepository();
